import { css } from 'aphrodite';
import * as React from 'react';
import { useCoachingAdherenceScoreboardQuery } from '../../../queries/AIConversation/useCoachingAdherenceScoreboardQuery';
import { useSalesCoachingCohortsQuery } from '../../../queries/AISalesCoaching/useSalesCoachingCohortsQuery';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Field, FieldLabel } from '../../../web/components-v2/Field';
import { IUser } from '../../../extViewmodels';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../web/components/UserCombobox';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { MIN_CALLS_FOR_SHARE } from '../../constants/coachingAdherence';
import {
	ALL_REPS_BOARD,
	COACH_MODE_OPTIONS,
	type CoachModeFilter,
	type GroupFilterOption,
	NO_COHORT_BOARD,
	NO_GROUP_FILTERS,
	type RepGroupFilters,
	applyGroupFilters,
	boardLabel,
	buildScoreboardRange,
	groupFilterOptions,
	sendsRepDetails,
	summarizeScoreboard,
	todayDateString,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { ScoreboardSummaryTiles } from './ScoreboardSummaryTiles';
import { ScoreboardTable } from './ScoreboardTable';
import { usePersistedScoreboardCohort } from './usePersistedScoreboardCohort';

interface IProps {
	onSelectCall: (aiConversationId: string) => void;
}

// Radix Select reserves the empty string for "no value", so "any" needs a real one.
const ANY = '__any__';

function GroupFilterSelect({
	id,
	label,
	anyLabel,
	value,
	options,
	onChange,
}: {
	id: string;
	label: string;
	anyLabel: string;
	value: string;
	options: GroupFilterOption[];
	onChange: (value: string) => void;
}) {
	return (
		<Field styles={[styleSheet.controlField]}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Select value={value || ANY} onValueChange={next => onChange(next === ANY ? '' : next)}>
				<SelectTrigger id={id} styles={styleSheet.groupTrigger}>
					<SelectValue>{options.find(option => option.value === value)?.label ?? anyLabel}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={ANY}>{anyLabel}</SelectItem>
					{options.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</Field>
	);
}

export function AdherenceScoreboard({ onSelectCall }: IProps) {
	const [storedCohort, setCohort] = usePersistedScoreboardCohort();
	const [startDate, setStartDate] = React.useState(todayDateString);
	const [endDate, setEndDate] = React.useState(todayDateString);
	const [pickedRep, setPickedRep] = React.useState<IUser | null>(null);
	const [groupFilters, setGroupFilters] = React.useState<RepGroupFilters>(NO_GROUP_FILTERS);

	const cohortsQuery = useSalesCoachingCohortsQuery();
	const cohorts = [
		...new Set((cohortsQuery.data ?? []).map(member => member.cohort).filter((name): name is string => Boolean(name))),
	].sort();
	const boards = [ALL_REPS_BOARD, NO_COHORT_BOARD, ...cohorts];
	const cohort = storedCohort && boards.includes(storedCohort) ? storedCohort : (cohorts[0] ?? NO_COHORT_BOARD);

	// The board is one cohort at a time, so picking a rep moves to their cohort (a rep in none moves to the no-cohort
	// board, unless the all-reps board already shows them) and picking a board drops the rep and the group filters. A rep
	// and a group filter would contradict each other, so choosing one clears the other.
	const onRepChange = (user: IUser | null) => {
		setPickedRep(user);
		setGroupFilters(NO_GROUP_FILTERS);
		if (!user || !cohortsQuery.data) {
			return;
		}
		const repCohort = cohortsQuery.data.find(member => member.userId === user.id)?.cohort;
		if (repCohort) {
			setCohort(repCohort);
		} else if (cohort !== ALL_REPS_BOARD) {
			setCohort(NO_COHORT_BOARD);
		}
	};
	const onCohortChange = (next: string) => {
		setCohort(next);
		setPickedRep(null);
		setGroupFilters(NO_GROUP_FILTERS);
	};
	const onGroupFilterChange = (change: Partial<RepGroupFilters>) => {
		setGroupFilters(current => ({ ...current, ...change }));
		setPickedRep(null);
	};

	const range = buildScoreboardRange(startDate, endDate);
	const validRange = 'error' in range ? null : range;
	const rangeError = 'error' in range ? range.error : null;
	// Wait for the cohort list, or the default board would load once before a remembered cohort could be confirmed.
	const scoreboardQuery = useCoachingAdherenceScoreboardQuery({
		cohort: cohortsQuery.data ? cohort : null,
		start: validRange?.start ?? null,
		end: validRange?.end ?? null,
	});

	const selectedRep = pickedRep ? (scoreboardQuery.data?.reps.find(rep => rep.userId === pickedRep.id) ?? null) : null;
	const options = groupFilterOptions(scoreboardQuery.data?.reps ?? []);
	// A new range rebuilds the options, so a value they no longer offer would filter invisibly; it's dropped instead.
	const offered = (value: string, list: GroupFilterOption[]) =>
		list.some(option => option.value === value) ? value : '';
	// The manager comes from a search of active users, not the board's own list, which keeps departed managers and ids
	// the goals sheet never resolved; a manager with no reps here leaves an empty board that says so.
	const showCoachMode = sendsRepDetails(scoreboardQuery.data?.reps ?? []);
	const activeFilters: RepGroupFilters = {
		managerUserId: groupFilters.managerUserId,
		quotaTeam: offered(groupFilters.quotaTeam, options.quotaTeams),
		role: offered(groupFilters.role, options.roles),
		coachMode: showCoachMode ? groupFilters.coachMode : '',
	};

	const renderBody = () => {
		if (cohortsQuery.isLoading || (validRange && scoreboardQuery.isLoading)) {
			return <LoadingSpinner type='large' />;
		}
		if (cohortsQuery.isError) {
			return <div className={css(styleSheet.state)}>Couldn&rsquo;t load coaching cohorts.</div>;
		}
		if (!validRange) {
			return null;
		}
		if (scoreboardQuery.isError || !scoreboardQuery.data) {
			return <div className={css(styleSheet.state)}>Couldn&rsquo;t load the adherence scoreboard.</div>;
		}
		const scoreboard = scoreboardQuery.data;
		if (scoreboard.reps.every(rep => rep.judgedCalls === 0)) {
			return (
				<div className={css(styleSheet.state)}>
					No calls were scored by the adherence judge in this range yet. A call is scored a few minutes after the rep
					logs its outcome.
				</div>
			);
		}
		if (pickedRep && !selectedRep) {
			const name = `${pickedRep.firstName ?? ''} ${pickedRep.lastName ?? ''}`.trim() || 'This rep';
			return <div className={css(styleSheet.state)}>{name} isn&rsquo;t on this scoreboard for this range.</div>;
		}
		const reps = selectedRep ? [selectedRep] : applyGroupFilters(scoreboard.reps, activeFilters);
		if (reps.length === 0) {
			return <div className={css(styleSheet.state)}>No reps on this board match these filters.</div>;
		}
		const summary = summarizeScoreboard(reps);
		return (
			<>
				<ScoreboardSummaryTiles
					summary={summary}
					reps={reps}
					selectedRep={selectedRep}
					followedScore={scoreboard.followedScore}
				/>
				<p className={css(styleSheet.headlineNote)}>
					A call counts as followed at a score of {scoreboard.followedScore} or more. Rep counts include only reps with
					at least {MIN_CALLS_FOR_SHARE} calls past the hook.
					{showCoachMode
						? ' Dials per demo counts prospecting and cold demo dials, as sales reporting does; Dials also counts confirm and reschedule dials. Coach mode is each rep’s setting today.'
						: null}
				</p>
				<ScoreboardTable
					key={selectedRep?.userId ?? 'all'}
					scoreboard={scoreboard}
					reps={reps}
					initialExpandedRepId={selectedRep?.userId ?? null}
					range={validRange}
					showCohort={cohort === ALL_REPS_BOARD}
					onSelectCall={onSelectCall}
				/>
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controls)}>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-cohort'>Cohort</FieldLabel>
					<Select value={cohort} onValueChange={onCohortChange} disabled={!cohortsQuery.data}>
						<SelectTrigger id='scoreboard-cohort' styles={styleSheet.cohortTrigger}>
							<SelectValue>{boardLabel(cohort)}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{boards.map(name => (
								<SelectItem key={name} value={name}>
									{boardLabel(name)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-rep'>Rep</FieldLabel>
					<UserCombobox
						id={pickedRep?.id}
						key={`rep-${pickedRep?.id ?? ''}`}
						onValueChange={(user: IUser | null) => onRepChange(user)}
					>
						<ComboboxInput
							id='scoreboard-rep'
							placeholder={cohortsQuery.data ? 'All reps' : 'Loading reps…'}
							showClear
							disabled={!cohortsQuery.data}
							styles={[styleSheet.cohortTrigger]}
						/>
						<ComboboxContent>
							<UserComboboxStatus />
							<ComboboxList>
								{(user: IUser) => <UserComboboxAvatarItem key={`rep-item-${user.id}`} user={user} />}
							</ComboboxList>
						</ComboboxContent>
					</UserCombobox>
				</Field>
				{options.managers.length > 0 ? (
					<Field styles={[styleSheet.controlField]}>
						<FieldLabel htmlFor='scoreboard-manager'>Manager</FieldLabel>
						<UserCombobox
							id={activeFilters.managerUserId || undefined}
							key={`manager-${activeFilters.managerUserId}`}
							onValueChange={(user: IUser | null) => onGroupFilterChange({ managerUserId: user?.id ?? '' })}
						>
							<ComboboxInput
								id='scoreboard-manager'
								placeholder='All managers'
								showClear
								styles={[styleSheet.cohortTrigger]}
							/>
							<ComboboxContent>
								<UserComboboxStatus />
								<ComboboxList>
									{(user: IUser) => <UserComboboxAvatarItem key={`manager-item-${user.id}`} user={user} />}
								</ComboboxList>
							</ComboboxContent>
						</UserCombobox>
					</Field>
				) : null}
				{options.quotaTeams.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-quota-team'
						label='Quota team'
						anyLabel='All teams'
						value={activeFilters.quotaTeam}
						options={options.quotaTeams}
						onChange={quotaTeam => onGroupFilterChange({ quotaTeam })}
					/>
				) : null}
				{showCoachMode ? (
					<GroupFilterSelect
						id='scoreboard-coach-mode'
						label='Coach mode'
						anyLabel='Any mode'
						value={activeFilters.coachMode}
						options={COACH_MODE_OPTIONS}
						onChange={coachMode => onGroupFilterChange({ coachMode: coachMode as CoachModeFilter })}
					/>
				) : null}
				{options.roles.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-role'
						label='Role'
						anyLabel='All roles'
						value={activeFilters.role}
						options={options.roles}
						onChange={role => onGroupFilterChange({ role })}
					/>
				) : null}
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-start'>Start</FieldLabel>
					<DateInput id='scoreboard-start' value={startDate} onChange={setStartDate} />
				</Field>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-end'>End</FieldLabel>
					<DateInput id='scoreboard-end' value={endDate} onChange={setEndDate} />
				</Field>
			</div>
			{rangeError ? (
				<div className={css(styleSheet.rangeError)} role='alert'>
					{rangeError}
				</div>
			) : null}
			{renderBody()}
		</div>
	);
}
